<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <!-- Customer info bar -->
      <div class="card mb-3">
        <div class="card-body py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 class="mb-0">{{ customerName || 'Customer' }} — Wallet Ledger</h4>
          </div>
          <div class="d-flex align-items-center flex-wrap gap-3">
            <select v-model="selectedBranchId" @change="fetchLedger(1)" class="form-control form-control-sm" style="width:180px;">
              <option value="">All Branches</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
            <span class="font-weight-bold" :class="balance >= 0 ? 'text-success' : 'text-danger'">
              Balance: {{ balance | currency }}
            </span>
            <button class="btn btn-sm btn-success" @click="openAdvanceModal">+ Collect Advance</button>
            <button class="btn btn-sm btn-outline-secondary" @click="$router.back()">← Back</button>
          </div>
        </div>
      </div>

      <!-- Transactions table -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between">
          <h3 class="mb-0">Transaction History</h3>
          <div v-if="loading" class="text-muted small">Loading…</div>
        </div>

        <el-table
          :data="transactions"
          class="table-responsive table-flush"
          header-row-class-name="thead-light"
          empty-text="No transactions found"
        >
          <el-table-column label="Date" width="160">
            <template slot-scope="scope">{{ scope.row.created_at | formatDate }}</template>
          </el-table-column>

          <el-table-column label="Type" width="110">
            <template slot-scope="scope">
              <span
                class="badge"
                :class="scope.row.type === 'credit' ? 'badge-success' : 'badge-danger'"
                style="font-size:0.8rem;padding:4px 10px;"
              >
                {{ scope.row.type === 'credit' ? 'Credit' : 'Debit' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="Amount" width="130">
            <template slot-scope="scope">
              <span :class="scope.row.type === 'credit' ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                {{ scope.row.type === 'credit' ? '+' : '-' }}{{ scope.row.amount | currency }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="Description">
            <template slot-scope="scope">{{ scope.row.description || '—' }}</template>
          </el-table-column>

          <el-table-column label="Reference" width="160">
            <template slot-scope="scope">
              <span v-if="scope.row.reference_type" class="text-muted small">
                {{ scope.row.reference_type }}
                <span v-if="scope.row.reference_id"><br />{{ scope.row.reference_id | truncateId }}</span>
              </span>
              <span v-else class="text-muted">—</span>
            </template>
          </el-table-column>
        </el-table>

        <div class="card-footer">
          <div v-if="meta" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
            <p class="card-category">
              Showing {{ meta.from || 0 }} to {{ meta.to || 0 }} of {{ meta.total || 0 }} entries
            </p>
            <pagination :meta="paginationMeta" @paginate="setPagination" />
          </div>
        </div>
      </div>
    </div>

    <!-- Collect Advance Modal -->
    <div
      v-if="advanceModal"
      style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:9999;"
      class="d-flex align-items-center justify-content-center"
    >
      <div class="bg-white rounded shadow-lg p-4" style="width:400px;max-width:95vw;">
        <h5 class="mb-3">Collect Advance — {{ customerName }}</h5>

        <div class="form-group mb-3">
          <label class="form-control-label">Amount (₹) <span class="text-danger">*</span></label>
          <input
            v-model.number="advance.amount"
            type="number"
            min="1"
            class="form-control"
            placeholder="Enter amount"
            ref="advanceAmountInput"
          />
        </div>

        <div class="form-group mb-3">
          <label class="form-control-label">Payment Mode</label>
          <select v-model="advance.paymentMode" class="form-control">
            <option value="">— Select —</option>
            <option value="Cash">Cash</option>
            <option value="UPI">UPI</option>
            <option value="Card">Card</option>
            <option value="Cheque">Cheque</option>
            <option value="Online">Online</option>
          </select>
        </div>

        <div class="form-group mb-3">
          <label class="form-control-label">Branch <span class="text-danger">*</span></label>
          <select v-model="advance.branchId" class="form-control">
            <option value="">— Select branch —</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <small class="text-muted">The branch whose account this advance is credited to.</small>
        </div>

        <div class="form-group mb-3">
          <label class="form-control-label">Note</label>
          <input v-model="advance.note" type="text" class="form-control" placeholder="Optional note" />
        </div>

        <p v-if="advanceError" class="text-danger text-sm mb-2">{{ advanceError }}</p>

        <div class="d-flex gap-2">
          <button @click="closeAdvanceModal" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="saveAdvance" :disabled="savingAdvance" class="btn btn-success btn-sm flex-fill">
            {{ savingAdvance ? 'Saving...' : 'Collect ₹' + (advance.amount || 0) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";

export default {
  name: "CustomerLedger",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    "header-component": headerComponent,
    pagination,
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    },
    truncateId(value) {
      if (!value) return "";
      return value.length > 12 ? value.slice(0, 8) + "…" : value;
    }
  },
  data() {
    return {
      balance: 0,
      customerName: "",
      transactions: [],
      meta: null,
      loading: false,
      currentPage: 1,
      perPage: 20,
      branches: [],
      selectedBranchId: "",
      advanceModal: false,
      savingAdvance: false,
      advanceError: "",
      advance: { amount: null, paymentMode: "", note: "", branchId: "" },
    };
  },
  computed: {
    userId() {
      return this.$route.params.userId;
    },
    paginationMeta() {
      if (!this.meta) return null;
      return {
        from: this.meta.from,
        to: this.meta.to,
        total: this.meta.total,
        perPage: this.meta.per_page,
        currentPage: this.meta.current_page,
        last: this.meta.last_page,
      };
    },
  },
  mounted() {
    this.fetchBranches().then(() => {
      // Pre-select first branch for a useful default view
      if (this.branches.length && !this.selectedBranchId) {
        this.selectedBranchId = this.branches[0].id;
      }
      this.fetchLedger();
    });
  },
  methods: {
    fetchBranches() {
      return apiHelper
        .call("get", endpoints.LARAVEL_BASE_URL + "/api/v1/branches")
        .then(res => { this.branches = (res.data && res.data.data) || res.data || []; })
        .catch(() => {});
    },
    fetchLedger(page = 1) {
      if (!this.selectedBranchId) {
        this.balance = 0;
        this.transactions = [];
        this.meta = null;
        return;
      }
      this.loading = true;
      const params = new URLSearchParams({
        branch_id: this.selectedBranchId,
        page,
        perPage: this.perPage,
      });
      apiHelper
        .call("get", endpoints.LARAVEL_BASE_URL + "/api/v1/customers/" + this.userId + "/wallet?" + params)
        .then(res => {
          this.balance = res.data.balance || 0;
          this.customerName = res.data.customer_name || "";
          const t = res.data.transactions;
          this.transactions = t.data || [];
          this.meta = t;
        })
        .catch(() => {
          this.$notify({ message: "Failed to load ledger.", type: "danger", title: "Error" });
        })
        .finally(() => { this.loading = false; });
    },
    setPagination(data) {
      this.perPage = data.perPage;
      this.currentPage = data.page;
      this.fetchLedger(data.page);
    },
    openAdvanceModal() {
      this.advance = {
        amount: null,
        paymentMode: "",
        note: "",
        branchId: this.selectedBranchId || "",
      };
      this.advanceError = "";
      this.advanceModal = true;
      this.$nextTick(() => {
        if (this.$refs.advanceAmountInput) this.$refs.advanceAmountInput.focus();
      });
    },
    closeAdvanceModal() {
      this.advanceModal = false;
    },
    saveAdvance() {
      this.advanceError = "";
      if (!this.advance.amount || this.advance.amount <= 0) {
        this.advanceError = "Please enter a valid amount.";
        return;
      }
      if (!this.advance.branchId) {
        this.advanceError = "Please select a branch.";
        return;
      }
      const description = [
        "Advance payment",
        this.advance.paymentMode ? "via " + this.advance.paymentMode : "",
        this.advance.note || "",
      ].filter(Boolean).join(" — ");

      this.savingAdvance = true;
      apiHelper
        .call("post", endpoints.LARAVEL_BASE_URL + "/api/v1/customers/" + this.userId + "/wallet/topup", {
          branch_id: this.advance.branchId,
          amount: this.advance.amount,
          description,
        })
        .then(res => {
          this.balance = res.data.balance || this.balance;
          // Credit the selected branch's ledger with payment mode
          apiHelper
            .call("post", endpoints.LARAVEL_BASE_URL + "/api/v1/branches/" + this.advance.branchId + "/transactions/credit", {
              amount: this.advance.amount,
              description: description + " — customer: " + this.customerName,
              reference_type: "advance",
              reference_id: this.userId,
              payment_mode: this.advance.paymentMode || null,
            })
            .catch(() => {});
          this.$notify({ message: "Advance collected successfully.", type: "success", title: "Done" });
          this.closeAdvanceModal();
          this.selectedBranchId = this.advance.branchId;
          this.fetchLedger(this.currentPage);
        })
        .catch(err => {
          const msg = (err && err.response && err.response.data && err.response.data.message) || "Failed to collect advance.";
          this.advanceError = msg;
        })
        .finally(() => { this.savingAdvance = false; });
    },
  },
};
</script>
